import { useTableSort } from "@features/mx/utils/useTableSort";
import { downloadExcelSheet } from "@features/mx/validations/api";
import { useValidationContext } from "@features/mx/validations/context";
import { useReadingsList } from "@features/mx/validations/hooks";
import { commonHeaders } from "@features/mx/validations/types";
import { GenericTable } from "@shared/components/GenericTable";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { TableAction } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Users } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import ListViewActions from "../../shared/ListViewActions";
import { AssignReaderModal } from "./AssignMeterReaderModal";
import { AssignRoutesModal } from "./AssignRouteModal";
import { logEvent } from "@shared/analytics/analytics";
import { useToast } from "@shared/hooks/use-toast";

function RevisitReadingList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState("");
  const [selectedPremises, setSelectedPremises] = useState<string[]>([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const { sortArray, sortConfig, handleSort } = useTableSort();
  const { setViewMode, meterRunId, setValidatorId, readingId } =
    useValidationContext();
  const remoteUtilityId = useRemoteUtilityId();
  const [isAssignModalOpen, setAssignModalOpen] = useState(false);
  const [isAssignReaderModalOpen, setAssignReaderModalOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null); // to pass route info to reader modal

  const { data: revisitReadingList } = useReadingsList({
    remoteUtilityId,
    meterRunId,
    page: currentPage,
    limit: PAGE_SIZE,
    ruleStatus: "REVISIT",
    searchData: searchValue,
    premises: selectedPremises,
    sortBy: sortArray,
    readingId,
  });

  const {toast} = useToast();

  const columns = commonHeaders;

  const actions: TableAction[] = [
    {
      label: "View",
      icon: "Eye",
      onClick: (row) => {
        const hasValidatorIds =
          Array.isArray(row?.validatorIds) && row.validatorIds.length > 0;

        if (hasValidatorIds) {
          const params = new URLSearchParams(searchParams);
          params.set("readingId", row?.id);
          setSearchParams(params);
          setViewMode("detail");
          setValidatorId(row.validatorIds);
        } else {
          toast({title: "Validator not assigned for this reading"});
          setValidatorId(null);
        }
      },
      variant: "ghost",
    },
  ];

  const handleExport = async () => {
    try {
      const res = await downloadExcelSheet({
        remoteUtilityId,
        meterRunId,
        fileName: "break_view_readings",
        ruleStatus: "REVISIT",
      });
      toast({
          title: "Export Initiated",
          description: res?.data?.message,
          variant: "default",
        });
      logEvent('MX Reading List Exported', {
              type: 'Revisit Readings',
              count: revisitReadingList?.count,
            })
    } catch (error) {
      toast({title: "Failed to download Excel", variant: 'destructive'});
    }
  };

  const pagination = {
    currentPage,
    totalPages: Math.ceil((revisitReadingList?.count || 0) / PAGE_SIZE),
    totalItems: revisitReadingList?.count || 0,
    itemsPerPage: PAGE_SIZE,
    startItem: (currentPage - 1) * PAGE_SIZE + 1,
    endItem: Math.min(currentPage * PAGE_SIZE, revisitReadingList?.count || 0),
  };

  return (
    <div className="space-y-4">
      <ListViewActions
        onExportClick={handleExport}
        onSearch={(val) => setSearchValue(val)}
        onRouteChange={(val) => setSelectedPremises(val)}
      />
      {selectedRows.length > 0 && (
        <div className="flex justify-end">
          <button
            className="bg-[#0F172A] hover:bg-[#1E293B] text-white font-medium py-2 px-4 rounded flex items-center gap-2"
            onClick={() => setAssignModalOpen(true)}
          >
            <Users className="w-4 h-3" />
            Assign All ({selectedRows.length})
          </button>
        </div>
      )}
      {revisitReadingList?.results && (
        <GenericTable
          data={revisitReadingList.results}
          columns={columns}
          rowKey="id"
          onSelectionChange={setSelectedRows}
          selectable={true}
          selectedRows={selectedRows}
          actions={actions}
          actionsType="icons"
          pagination={pagination}
          onPageChange={setCurrentPage}
          sortConfig={sortConfig}
          onSort={handleSort}
          emptyMessage="No readings found"
        />
      )}
      <>
        {isAssignModalOpen && (
          <AssignRoutesModal
            onClose={() => setAssignModalOpen(false)}
            onAssignReader={(route) => {
              setSelectedRoute(route);
              setAssignReaderModalOpen(true);
            }}
            selectedRows={[
              ...new Set(
                revisitReadingList?.results
                  ?.filter((item) => selectedRows.includes(item.id.toString()))
                  .map((item) => item.cycleId)
              ),
            ]}
          />
        )}

        {isAssignReaderModalOpen && selectedRoute && (
          <AssignReaderModal
            route={selectedRoute}
            onClose={() => setAssignReaderModalOpen(false)}
          />
        )}
      </>
    </div>
  );
}

export default RevisitReadingList;
